import React, { useEffect, useRef, useState } from 'react';
import './_StatusPill.scss';

const STATUS_CONFIG = {
  active: {
    label: 'Active',
    description: 'Work is currently underway',
    dotColor: '#48af98',
    pillBg: 'rgba(72,175,152,0.12)',
    pillColor: '#2a7a69',
    pillBorder: 'rgba(72,175,152,0.4)',
    bannerBg: 'rgba(72,175,152,0.1)',
    bannerBorder: 'rgba(72,175,152,0.35)',
    bannerColor: '#2a7a69',
    confirmBg: '#48af98',
  },
  pending: {
    label: 'Pending',
    description: 'Awaiting approval or next phase',
    dotColor: '#FF9F43',
    pillBg: 'rgba(255,159,67,0.1)',
    pillColor: '#b86a00',
    pillBorder: 'rgba(255,159,67,0.4)',
    bannerBg: 'rgba(255,159,67,0.1)',
    bannerBorder: 'rgba(255,159,67,0.35)',
    bannerColor: '#b86a00',
    confirmBg: '#e08800',
  },
  archived: {
    label: 'Archived',
    description: 'Project is closed out',
    dotColor: '#cecac2',
    pillBg: '#e8e4df',
    pillColor: '#002230',
    pillBorder: '#cecac2',
    bannerBg: '#f0ece7',
    bannerBorder: '#cecac2',
    bannerColor: '#002230',
    confirmBg: '#555e63',
  },
};

export default function StatusPill({ initialStatus = 'active', onStatusChange }) {
  const [status, setStatus] = useState(initialStatus);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const handleSelect = (key) => {
    setIsOpen(false);
    if (key !== status) setPendingStatus(key);
  };

  const handleConfirm = async () => {
    const previous = status;
    const next = pendingStatus;
    setStatus(next);
    setPendingStatus(null);
    try {
      await onStatusChange?.(next);
    } catch {
      setStatus(previous);
    }
  };

  const handleCancel = () => setPendingStatus(null);

  const current = STATUS_CONFIG[status];
  const pending = pendingStatus ? STATUS_CONFIG[pendingStatus] : null;

  return (
    <div ref={containerRef} className='status-pill'>
      <button
        type='button'
        className='status-pill__trigger'
        onClick={() => setIsOpen((v) => !v)}
        style={{
          background: current.pillBg,
          color: current.pillColor,
          borderColor: current.pillBorder,
        }}
      >
        <span
          className='status-pill__dot'
          style={{ background: current.dotColor }}
        />
        {current.label}
        <svg
          className={`status-pill__chevron${isOpen ? ' status-pill__chevron--open' : ''}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {isOpen && (
        <div className='status-pill__dropdown'>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              type='button'
              className='status-pill__option'
              onClick={() => handleSelect(key)}
            >
              <span
                className='status-pill__dot'
                style={{ background: cfg.dotColor, marginTop: '0.25rem' }}
              />
              <span style={{ flex: 1 }}>
                <span className='status-pill__option-label'>{cfg.label}</span>
                {cfg.description && (
                  <span className='status-pill__option-desc'>{cfg.description}</span>
                )}
              </span>
              {key === status && (
                <svg
                  className='status-pill__option-check'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 13l4 4L19 7' />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}

      {pending && (
        <div
          className='status-pill__banner'
          style={{
            background: pending.bannerBg,
            borderColor: pending.bannerBorder,
            color: pending.bannerColor,
          }}
        >
          <span
            className='status-pill__dot'
            style={{ background: pending.dotColor }}
          />
          <span className='status-pill__banner-label'>
            Set to <strong>{pending.label}</strong>?
          </span>
          <button
            type='button'
            className='status-pill__btn status-pill__btn--cancel'
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type='button'
            className='status-pill__btn status-pill__btn--confirm'
            onClick={handleConfirm}
            style={{ background: pending.confirmBg }}
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}
